import { useQueryParams } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { Segment } from '@calendly/segment';
import { BareButton } from '@calendly/ui/components/bare-button';
import { IconCalendarPlus } from '@calendly/ui/components/glyphs/IconCalendarPlus';
import { Tooltip } from '@calendly/ui/components/tooltip';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import classNames from 'classnames';
import { snakeCase } from 'lodash-es';
import moment from 'moment-timezone';
import { useCallback, useEffect, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { setGetParams } from 'actions/core/navigation';
import { openPollsBookingModal } from 'actions/polls/scheduling';
import { useGetUserQuery } from 'common';
import { formatTime } from 'services/time_format';
import iconThumbsUp from './icon_thumb_up.svg';
import { WarningsSection } from './warnings';
import { getUniqInvitees } from '../../helpers';
const spotButtonActiveCss = "s10o7ede";
const spotButtonCss = "s1hzpbo6";
const SpotButton = forwardRef((props, ref) => {
  const {
    active,
    ...restProps
  } = props;
  return <BareButton ref={ref} {...restProps} className={classNames(spotButtonCss, {
    [spotButtonActiveCss]: active
  }, props.className)} />;
});
const votesContainerCss = "v1iby4ho";
const VotesContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(votesContainerCss, props.className)} />;
});
const timeContainerCss = "t129vwlx";
const TimeContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(timeContainerCss, props.className)} />;
});
const bookContainerCss = "b1ys1uif";
const BookContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(bookContainerCss, props.className)} />;
});
const recursiveSnakeize = item => {
  if (Array.isArray(item)) {
    return item.map(el => recursiveSnakeize(el));
  }
  if (typeof item === 'function' || item !== Object(item)) {
    return item;
  }
  return Object.fromEntries(Object.entries(item).map(([key, value]) => [snakeCase(key), recursiveSnakeize(value)]));
};
export const Spot = ({
  spot,
  poll,
  isTopSpot = false
}) => {
  const {
    data: {
      timezone,
      timeNotation
    }
  } = useGetUserQuery();
  const {
    booking_time: paramSpotTime
  } = useQueryParams();
  const dispatch = useDispatch();
  const isSpotInParams = paramSpotTime && moment(spot.startTime).isSame(paramSpotTime);
  const openBookingModal = useCallback(() => {
    dispatch(openPollsBookingModal({
      name: poll.name,
      eventTypeId: poll.id,
      locationConfiguration: recursiveSnakeize(poll.locationConfigurations[0]),
      invitees: recursiveSnakeize(getUniqInvitees(poll)),
      startTime: spot.startTime,
      endTime: spot.endTime,
      hasExternalEventConflict: spot.availabilityCode === 'external',
      internalConflictEvents: recursiveSnakeize(spot.internalConflictEvents)
    }));
  }, [dispatch, poll, spot]);
  const formattedTime = formatTime(moment(spot.startTime).tz(timezone), timeNotation);
  const isSpotExpired = moment().isSameOrAfter(spot.startTime);
  const isPollActive = poll.status === 'created';
  const isInternalConflict = ['internal', 'reserved', 'buffer'].includes(spot.availabilityCode);
  const isDoubleBookable = ['internal', 'buffer'].includes(spot.availabilityCode);
  const cohostHasConflict = !!spot.membersWithConflicts?.length;
  const allowSpotBooking = !isInternalConflict || isDoubleBookable;
  const showBookingButton = allowSpotBooking && isPollActive && !isSpotExpired && !cohostHasConflict && spot.invitees.length > 0;
  useEffect(() => {
    if (isSpotInParams) {
      openBookingModal();
      Segment.track('polls_host_email.book_time_click');
      dispatch(setGetParams({
        booking_time: null
      }));
    }
  }, [dispatch, isSpotInParams, openBookingModal]);
  return <SpotButton {...showBookingButton ? {
    active: true,
    'aria-label': I18n.t('publisher.scheduling.polls.book_at', {
      time: formattedTime,
      count: spot.invitees.length
    }),
    onClick: openBookingModal
  } : {
    as: 'div',
    type: null
  }}>
      <Tooltip text={spot.invitees.map(({
      fullName
    }) => fullName).join(', ')}>
        <VotesContainer>
          <img src={iconThumbsUp} alt="" />
          {spot.invitees.length}
        </VotesContainer>
      </Tooltip>
      <TimeContainer>
        <div>
          {formattedTime}
        </div>
        <WarningsSection isSpotExpired={isSpotExpired} isPollActive={isPollActive} allowSpotBooking={allowSpotBooking} isTopSpot={isTopSpot} spot={spot} cohostHasConflict={cohostHasConflict} />
      </TimeContainer>
      {showBookingButton && <BookContainer>
          <UiIcon icon={IconCalendarPlus} size="small" />
          {I18n.t('publisher.scheduling.polls.book')}
        </BookContainer>}
    </SpotButton>;
};

require("./spot.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./spot.js");